/**
 * Raw Shopify client operations.
 * No React, no hooks — pure async functions wrapping the shopify-buy SDK.
 */
import { resolveConfig, isBrandKey, type BrandKey } from '@/config/configService';
import Client from 'shopify-buy';
import { AddressSchema, type Checkout } from '@/lib/schemas/shopify';
import axiosObj from '@/store/axios';

// ---------------------------------------------------------------------------
// Client cache (per-vendor, lazily initialised)
// ---------------------------------------------------------------------------

function getVendorFromPath(): string {
  if (typeof window === 'undefined') return 'nestingale';
  const segment = window.location.pathname.split('/')[1] || 'nestingale';
  return isBrandKey(segment) ? segment : 'nestingale';
}

const clientCache: Record<string, any> = {};

export function getVendorClient(vendor?: string): any {
  const vendorName = vendor ?? getVendorFromPath();
  if (clientCache[vendorName]) return clientCache[vendorName];

  const config = resolveConfig('shopify', vendorName as BrandKey);
  try {
    clientCache[vendorName] = Client.buildClient({
      domain: config.domain,
      storefrontAccessToken: config.storefrontAccessToken,
      apiVersion: '2024-01',
    });
    console.log(`✅ [ShopifyApi] Client created for vendor "${vendorName}"`);
  } catch (error) {
    console.error(`❌ [ShopifyApi] Failed to create client for "${vendorName}":`, error);
    clientCache[vendorName] = null;
  }
  return clientCache[vendorName];
}

// Default (nestingale) client for backward-compat callers
let _defaultClient: any;
try {
  const cfg = resolveConfig('shopify', 'nestingale' as BrandKey);
  _defaultClient = Client.buildClient({
    domain: cfg.domain,
    storefrontAccessToken: cfg.storefrontAccessToken,
    apiVersion: '2024-01',
  });
} catch (e) {
  console.error('❌ [ShopifyApi] Failed to initialise default client:', e);
  _defaultClient = null;
}

export function getDefaultClient(): any {
  return _defaultClient;
}

// ---------------------------------------------------------------------------
// Cart / checkout operations
// ---------------------------------------------------------------------------

/** Fetch or create a Shopify checkout. Returns plain JSON. */
export async function fetchOrCreateCheckout(checkoutId?: string | null): Promise<Checkout> {
  const client = getDefaultClient();
  if (!client?.checkout) throw new Error('Shopify client.checkout not available');

  let id = checkoutId;

  if (!id) {
    const created = await client.checkout.create();
    id = created.id;
  }

  let details = await client.checkout.fetch(id);
  if (!details) {
    // Cart expired — create a fresh one
    details = await client.checkout.create();
  }

  return JSON.parse(JSON.stringify(details)) as Checkout;
}

export interface LineItemInput {
  variantId: string;
  quantity: number;
  /** Metadata passed to the Shopify Function for discount calculation.
   *  Use `_target_price` + `_price_signature` for the dynamic-pricing path. */
  customAttributes?: { key: string; value: string }[];
}

/** Add line items to an existing checkout. */
export async function addLineItemsToCheckout(
  checkoutId: string,
  lineItemsToAdd: LineItemInput[],
): Promise<Checkout> {
  const client = getDefaultClient();
  const filtered = lineItemsToAdd.filter(Boolean);
  const response = await client.checkout.addLineItems(checkoutId, filtered);
  return JSON.parse(JSON.stringify(response)) as Checkout;
}

/** Create a fresh checkout and add line items (used to recreate stale carts). */
export async function recreateAndAddLineItems(
  lineItemsToAdd: { variantId: string; quantity: number }[],
): Promise<Checkout> {
  const client = getDefaultClient();
  const newCheckout = await client.checkout.create();
  const filtered = lineItemsToAdd.filter(Boolean);
  const response = await client.checkout.addLineItems(newCheckout.id, filtered);
  return JSON.parse(JSON.stringify(response)) as Checkout;
}

/** Update quantity for a single line item. */
export async function updateLineItemQuantity(
  checkoutID: string,
  lineItemId: string,
  quantity: number,
): Promise<Checkout> {
  const client = getDefaultClient();
  const resp = await client.checkout.updateLineItems(checkoutID, [
    { id: lineItemId, quantity: parseInt(String(quantity), 10) },
  ]);
  return JSON.parse(JSON.stringify(resp)) as Checkout;
}

/** Update quantities for multiple line items. */
export async function updateLineItemsQuantity(
  checkoutID: string,
  lineItemsToUpdate: { id: string; quantity: number }[],
): Promise<Checkout> {
  const client = getDefaultClient();
  const resp = await client.checkout.updateLineItems(checkoutID, lineItemsToUpdate);
  return JSON.parse(JSON.stringify(resp)) as Checkout;
}

/** Remove a single line item from the checkout. */
export async function removeLineItem(checkoutID: string, lineItemId: string): Promise<Checkout> {
  const client = getDefaultClient();
  const resp = await client.checkout.removeLineItems(checkoutID, [lineItemId]);
  return JSON.parse(JSON.stringify(resp)) as Checkout;
}

/** Update the email on a checkout. */
export async function updateCheckoutEmail(
  checkoutId: string,
  email: string | undefined,
): Promise<Checkout | undefined> {
  if (!email) return undefined;
  const client = getDefaultClient();
  const response = await client.checkout.updateEmail(checkoutId, email);
  return JSON.parse(JSON.stringify(response)) as Checkout;
}

/** Update the shipping address on a checkout. */
export async function updateCheckoutShippingAddress(
  checkoutId: string,
  shippingAddress: { addressList?: any[] },
): Promise<Checkout | undefined> {
  const list = shippingAddress?.addressList;
  if (!list || list.length === 0) return undefined;

  const defaultAddr = list.find((a) => a.default) ?? list[0];
  const parsed = AddressSchema.parse(defaultAddr);

  const payload = {
    address1: parsed.address1,
    address2: parsed.address2,
    city: parsed.city,
    company: parsed.company,
    country: parsed.country,
    firstName: parsed.first_name ?? parsed.firstName,
    lastName: parsed.last_name ?? parsed.lastName,
    phone: parsed.phone,
    province: parsed.province,
    zip: parsed.zip,
  };

  const client = getDefaultClient();
  const response = await client.checkout.updateShippingAddress(checkoutId, payload);
  return JSON.parse(JSON.stringify(response)) as Checkout;
}

/** Fetch customer shipping addresses from the Nestingale API. */
export async function getCustomerShippingAddress(
  customerId: string,
  brand?: string,
): Promise<any> {
  let url = `/api/customer/addresses?userId=${customerId}`;
  if (brand && brand !== 'nestingale') url += `&vendor=${brand}`;
  const response = await axiosObj.get(url);
  return response.data;
}

/** Fetch shop info. */
export async function fetchShopInfo(): Promise<any> {
  const client = getDefaultClient();
  return client.shop.fetchInfo();
}

/** Fetch all products (legacy, rarely used). */
export async function fetchAllProducts(): Promise<any[]> {
  const client = getDefaultClient();
  const products = await client.product.fetchAll();
  return products.map((p: any) => ({
    id: p.id,
    name: p.title,
    description: p.description,
    price: {
      amount: p.variants[0].price.amount,
      currencyCode: p.variants[0].price.currencyCode,
      type: p.variants[0].price.type,
    },
    image: p.images[0].src,
  }));
}

/** Fetch a single product by ID. */
export async function fetchProduct(id: string): Promise<any> {
  const client = getDefaultClient();
  return client.product.fetch(id);
}
